import React, { useEffect, useState, useCallback } from 'react';
import { getMessages, markMessageRead } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import {
  MessageSquare,
  Mail,
  MailOpen,
  Clock,
  User
} from 'lucide-react';

const MessagesList = () => {
  const { t, locale } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchMessages = useCallback(async () => {
    try {
      const data = await getMessages();
      setMessages(data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleMessageClick = useCallback(async (message) => {
    setSelectedMessage(message);
    if (!message.is_read) {
      try {
        await markMessageRead(message.id);
        setMessages((prev) =>
          prev.map((m) => (m.id === message.id ? { ...m, is_read: true } : m))
        );
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-messages">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2E2E2E]" data-testid="client-messages-title"><span className="gradient-text">{t('portalUi.client.messages.title', 'Ziņojumi')}</span></h1>
        <p className="text-[#2E2E2E]/60 mt-1" data-testid="client-messages-subtitle">
          {t('portalUi.client.messages.subtitle', 'Paziņojumi un ziņas no administrācijas')}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <div className="lg:col-span-1">
          <Card className="border-0 shadow-sm card-shine" data-testid="client-messages-list">
            <CardHeader className="pb-2">
              <CardTitle className="text-base" data-testid="client-messages-inbox-title">{t('portalUi.client.messages.inboxTitle', 'Ienākošā paste')}</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              {messages.length > 0 ? (
                <div className="space-y-1">
                  {messages.map((msg) => (
                    <button
                      key={msg.id}
                      onClick={() => handleMessageClick(msg)}
                      className={`w-full text-left p-3 rounded-lg transition-colors hover-lift ${
                        selectedMessage?.id === msg.id
                          ? 'bg-[#1F3D2B] text-white'
                          : msg.is_read
                            ? 'hover:bg-gray-100'
                            : 'bg-blue-50 hover:bg-blue-100'
                      }`}
                      data-testid={`client-message-${msg.id}`}
                    >
                      <div className="flex items-start gap-3">
                        {msg.is_read ? (
                          <MailOpen className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            selectedMessage?.id === msg.id ? 'text-white/70' : 'text-[#2E2E2E]/40'
                          }`} />
                        ) : (
                          <Mail className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            selectedMessage?.id === msg.id ? 'text-white' : 'text-blue-600'
                          }`} />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${
                            selectedMessage?.id === msg.id ? 'text-white' : 'text-[#2E2E2E]'
                          }`}>
                            {msg.subject}
                          </p>
                          <p className={`text-xs mt-1 ${
                            selectedMessage?.id === msg.id ? 'text-white/70' : 'text-[#2E2E2E]/60'
                          }`}>
                            {new Date(msg.created_at).toLocaleDateString(locale)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8" data-testid="client-messages-empty">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-[#2E2E2E]/20" />
                  <p className="text-[#2E2E2E]/60">{t('portalUi.client.messages.emptyList', 'Nav ziņojumu')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2">
          <Card className="border-0 shadow-sm h-full card-shine" data-testid="client-messages-detail">
            {selectedMessage ? (
              <>
                <CardHeader className="border-b border-gray-100">
                  <CardTitle className="text-lg" data-testid="client-messages-selected-subject">{selectedMessage.subject}</CardTitle>
                  <div className="flex items-center gap-4 text-sm text-[#2E2E2E]/60 mt-2">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {selectedMessage.sender_name}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {new Date(selectedMessage.created_at).toLocaleString(locale)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-[#2E2E2E] whitespace-pre-wrap leading-relaxed" data-testid="client-messages-selected-content">
                    {selectedMessage.content}
                  </p>
                </CardContent>
              </>
            ) : (
              <CardContent className="p-12 text-center h-full flex flex-col items-center justify-center" data-testid="client-messages-empty-detail">
                <Mail className="w-16 h-16 text-[#2E2E2E]/20 mb-4" />
                <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">{t('portalUi.client.messages.emptyDetailTitle', 'Izvēlieties ziņojumu')}</h3>
                <p className="text-[#2E2E2E]/60">
                  {t('portalUi.client.messages.emptyDetailText', 'Noklikšķiniet uz ziņojuma, lai to izlasītu')}
                </p>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MessagesList;
